import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Checkbox } from 'components/ui/checkbox'
import { Badge } from 'components/ui/badge'
import { Select } from 'components/ui/select'
import {
	initializeTemplates,
	initializeTemplateRisks,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	createTemplateRisk,
	deleteTemplateRisk,
} from 'slices/templateSlice'
import {
	GripVertical,
	Plus,
	Trash2,
	LayoutTemplate,
	Loader2,
	ChevronDown,
	ChevronRight,
	ShieldAlert,
} from 'lucide-react'
import notify from 'components/ui/notify'

// ─── Types ──────────────────────────────────────────────────────────────────────

interface RiskData {
	_id: string
	name: string
	category: string
	description: string
	defaultLikelihood: number
	defaultImpact: number
	suggestedMitigation: string
	suggestedContingency: string
	isNew?: boolean
}

const SCALE = [1, 2, 3, 4, 5]

// ─── SortableRow ────────────────────────────────────────────────────────────────

interface SortableRowProps {
	id: string
	seq: number
	risk: RiskData
	expanded: boolean
	onToggleExpanded: (id: string) => void
	onNameChange: (id: string, value: string) => void
	onCategoryChange: (id: string, value: string) => void
	onLikelihoodChange: (id: string, value: number) => void
	onImpactChange: (id: string, value: number) => void
	onDescriptionChange: (id: string, value: string) => void
	onMitigationChange: (id: string, value: string) => void
	onContingencyChange: (id: string, value: string) => void
	onDelete: (id: string) => void
	isSelected: boolean
	onSelect: (id: string, checked: boolean) => void
}

const riskScoreColor = (l: number, i: number) => {
	const score = l * i
	if (score >= 16) return 'bg-red-100 text-red-700'
	if (score >= 9) return 'bg-amber-100 text-amber-700'
	if (score >= 4) return 'bg-yellow-100 text-yellow-700'
	return 'bg-emerald-100 text-emerald-700'
}

const SortableRow = ({
	id,
	seq,
	risk,
	expanded,
	onToggleExpanded,
	onNameChange,
	onCategoryChange,
	onLikelihoodChange,
	onImpactChange,
	onDescriptionChange,
	onMitigationChange,
	onContingencyChange,
	onDelete,
	isSelected,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	const score = risk.defaultLikelihood * risk.defaultImpact

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			<div className="grid grid-cols-[24px_24px_32px_1fr_140px_70px_70px_60px_32px_32px] gap-1.5 py-1 px-1.5 items-center">
				<div
					{...attributes}
					{...listeners}
					className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
				>
					<GripVertical className="w-3.5 h-3.5" />
				</div>

				<button
					type="button"
					onClick={() => onToggleExpanded(id)}
					title={expanded ? 'Collapse details' : 'Expand details'}
					aria-label={expanded ? 'Collapse details' : 'Expand details'}
					className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 p-0"
				>
					{expanded ? (
						<ChevronDown className="w-3.5 h-3.5" />
					) : (
						<ChevronRight className="w-3.5 h-3.5" />
					)}
				</button>

				<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

				<Input
					value={risk.name}
					onChange={e => onNameChange(id, e.target.value)}
					className="h-7 text-xs"
					placeholder="Risk name (e.g. Anchor tenant non-performance)"
				/>

				<Input
					value={risk.category}
					onChange={e => onCategoryChange(id, e.target.value)}
					className="h-7 text-xs"
					placeholder="Category (e.g. Lease, Market)"
				/>

				<Select
					value={risk.defaultLikelihood}
					onChange={e => onLikelihoodChange(id, parseInt(e.target.value, 10) || 1)}
					className="h-7 text-xs"
					title="Likelihood (1 = rare, 5 = certain)"
				>
					{SCALE.map(n => (
						<option key={n} value={n}>
							L {n}
						</option>
					))}
				</Select>

				<Select
					value={risk.defaultImpact}
					onChange={e => onImpactChange(id, parseInt(e.target.value, 10) || 1)}
					className="h-7 text-xs"
					title="Impact (1 = minor, 5 = severe)"
				>
					{SCALE.map(n => (
						<option key={n} value={n}>
							I {n}
						</option>
					))}
				</Select>

				<Badge
					className={`text-[10px] justify-center font-semibold ${riskScoreColor(
						risk.defaultLikelihood,
						risk.defaultImpact,
					)}`}
					title="Risk score = likelihood × impact"
				>
					{score}
				</Badge>

				<div className="flex items-center justify-center">
					<Checkbox
						checked={isSelected}
						onCheckedChange={checked => onSelect(id, !!checked)}
					/>
				</div>

				<button
					type="button"
					title="Delete risk"
					onClick={() => onDelete(id)}
					className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
				>
					<Trash2 className="w-3.5 h-3.5" />
				</button>
			</div>

			{expanded && (
				<div className="px-3 pb-3 pt-1 bg-gray-50 grid grid-cols-1 md:grid-cols-3 gap-2">
					<div>
						<label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
							Description
						</label>
						<Textarea
							value={risk.description}
							onChange={e => onDescriptionChange(id, e.target.value)}
							rows={3}
							className="text-xs min-h-[5rem]"
							placeholder="What this risk represents (optional)"
						/>
					</div>
					<div>
						<label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
							Suggested Mitigation
						</label>
						<Textarea
							value={risk.suggestedMitigation}
							onChange={e => onMitigationChange(id, e.target.value)}
							rows={3}
							className="text-xs min-h-[5rem]"
							placeholder="How to reduce likelihood or impact"
						/>
					</div>
					<div>
						<label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
							Suggested Contingency
						</label>
						<Textarea
							value={risk.suggestedContingency}
							onChange={e => onContingencyChange(id, e.target.value)}
							rows={3}
							className="text-xs min-h-[5rem]"
							placeholder="What to do if it materializes"
						/>
					</div>
				</div>
			)}
		</div>
	)
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const RiskTemplateCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { entities: templateEntities, ids: templateIds, isInitialized } = useSelector(
		(state: any) => state.templates,
	)
	const {
		entities: riskEntities,
		ids: riskIds,
		isLoading: risksLoading,
	} = useSelector((state: any) => state.templates.risks)

	const riskTemplates = useMemo(() => {
		const all = (templateIds || []).map((id: string) => templateEntities[id]).filter(Boolean)
		return all
			.filter((t: any) => t.templateType === 'risk')
			.sort((a: any, b: any) =>
				(a.templateName || '').localeCompare(b.templateName || ''),
			)
	}, [templateIds, templateEntities])

	// ─── Editor State ───────────────────────────────────────────────────────────

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [riskOrder, setRiskOrder] = useState<string[]>([])
	const [risksMap, setRisksMap] = useState<Record<string, RiskData>>({})
	const [selectedRisks, setSelectedRisks] = useState<Set<string>>(new Set())
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [originalRiskIds, setOriginalRiskIds] = useState<Set<string>>(new Set())
	const nextTempId = useRef(0)

	// ─── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		;(dispatch as any)(initializeTemplates())
	}, [dispatch])

	useEffect(() => {
		if (selectedTemplateId) {
			;(dispatch as any)(initializeTemplateRisks(selectedTemplateId))
		}
	}, [dispatch, selectedTemplateId])

	useEffect(() => {
		if (!selectedTemplateId || isCreateMode) return
		const template = templateEntities[selectedTemplateId]
		if (!template) return

		setTemplateName(template.templateName || '')
		setDescription(template.description || '')
		setAccessLevel(template.accessLevel || 'private')

		const loaded = (riskIds || [])
			.map((id: string) => riskEntities[id])
			.filter(Boolean)
			.sort((a: any, b: any) => (a.seq || 0) - (b.seq || 0))

		const order: string[] = []
		const map: Record<string, RiskData> = {}
		const origIds = new Set<string>()
		loaded.forEach((r: any) => {
			order.push(r._id)
			map[r._id] = {
				_id: r._id,
				name: r.name || '',
				category: r.category || '',
				description: r.description || '',
				defaultLikelihood:
					typeof r.defaultLikelihood === 'number' ? r.defaultLikelihood : 3,
				defaultImpact: typeof r.defaultImpact === 'number' ? r.defaultImpact : 3,
				suggestedMitigation: r.suggestedMitigation || '',
				suggestedContingency: r.suggestedContingency || '',
			}
			origIds.add(r._id)
		})
		setRiskOrder(order)
		setRisksMap(map)
		setOriginalRiskIds(origIds)
		setSelectedRisks(new Set())
		setExpandedIds(new Set())
		setErrors({})
		setIsDirty(false)
	}, [riskIds, riskEntities, selectedTemplateId, isCreateMode, templateEntities])

	// ─── DnD ────────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setRiskOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
			setIsDirty(true)
		}
	}

	// ─── Row Handlers ───────────────────────────────────────────────────────────

	const toggleExpanded = (id: string) => {
		setExpandedIds(prev => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const patchRisk = (id: string, patch: Partial<RiskData>) => {
		setRisksMap(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }))
		setIsDirty(true)
	}

	const handleSelect = (id: string, checked: boolean) => {
		const newSet = new Set(selectedRisks)
		if (checked) newSet.add(id)
		else newSet.delete(id)
		setSelectedRisks(newSet)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelectedRisks(checked ? new Set(riskOrder) : new Set())
	}

	const isAllSelected =
		riskOrder.length > 0 && riskOrder.every(id => selectedRisks.has(id))

	const handleDeleteRisk = (id: string) => {
		const risk = risksMap[id]
		const name = risk?.name?.trim() || 'this risk'
		notify({
			title: 'Delete Risk',
			text: `Are you sure you want to delete "${name}"?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setRiskOrder(prev => prev.filter(rid => rid !== id))
				setRisksMap(prev => {
					const next = { ...prev }
					delete next[id]
					return next
				})
				setSelectedRisks(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setExpandedIds(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setIsDirty(true)
			}
		})
	}

	const handleDeleteSelected = () => {
		if (selectedRisks.size === 0) return
		notify({
			title: 'Delete Selected Risks',
			text: `Are you sure you want to delete ${selectedRisks.size} risk${
				selectedRisks.size > 1 ? 's' : ''
			}?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setRiskOrder(prev => prev.filter(id => !selectedRisks.has(id)))
				setRisksMap(prev => {
					const next = { ...prev }
					selectedRisks.forEach(id => delete next[id])
					return next
				})
				setSelectedRisks(new Set())
				setIsDirty(true)
			}
		})
	}

	const handleAddRisk = () => {
		const tempId = `_new_${nextTempId.current++}`
		const newRisk: RiskData = {
			_id: tempId,
			name: '',
			category: '',
			description: '',
			defaultLikelihood: 3,
			defaultImpact: 3,
			suggestedMitigation: '',
			suggestedContingency: '',
			isNew: true,
		}
		setRiskOrder(prev => [...prev, tempId])
		setRisksMap(prev => ({ ...prev, [tempId]: newRisk }))
		setExpandedIds(prev => new Set(prev).add(tempId)) // auto-expand new rows
		setIsDirty(true)
	}

	// ─── Template List Handlers ─────────────────────────────────────────────────

	const switchTemplate = useCallback((templateId: string | null, createMode: boolean) => {
		setSelectedTemplateId(templateId)
		setIsCreateMode(createMode)
		if (createMode) {
			setTemplateName('')
			setDescription('')
			setAccessLevel('private')
			setRiskOrder([])
			setRisksMap({})
			setOriginalRiskIds(new Set())
			setSelectedRisks(new Set())
			setExpandedIds(new Set())
			setErrors({})
			setIsDirty(false)
		}
	}, [])

	const handleTemplateClick = useCallback(
		(templateId: string) => {
			if (templateId === selectedTemplateId) return
			const doSwitch = () => switchTemplate(templateId, false)
			if (isDirty) {
				;notify({
					text: 'You have unsaved changes. Discard them?',
					buttons: ['Cancel', 'Discard'],
					dangerMode: true,
				}).then((value: any) => {
					if (value) doSwitch()
				})
			} else {
				doSwitch()
			}
		},
		[selectedTemplateId, isDirty, switchTemplate],
	)

	const handleNewTemplate = useCallback(() => {
		const doSwitch = () => switchTemplate(null, true)
		if (isDirty) {
			;notify({
				text: 'You have unsaved changes. Discard them?',
				buttons: ['Cancel', 'Discard'],
				dangerMode: true,
			}).then((value: any) => {
				if (value) doSwitch()
			})
		} else {
			doSwitch()
		}
	}, [isDirty, switchTemplate])

	const handleDeleteTemplate = useCallback(
		(e: React.MouseEvent, templateId: string) => {
			e.stopPropagation()
			const template = templateEntities[templateId]
			;notify({
				text: `Delete template "${template?.templateName}"? This cannot be undone.`,
				buttons: ['Cancel', 'Delete'],
				dangerMode: true,
			}).then(async (value: any) => {
				if (value) {
					await (dispatch as any)(deleteTemplate(templateId))
					;(dispatch as any)(initializeTemplates())
					if (selectedTemplateId === templateId) {
						switchTemplate(null, false)
					}
				}
			})
		},
		[dispatch, templateEntities, selectedTemplateId, switchTemplate],
	)

	// ─── Save ───────────────────────────────────────────────────────────────────

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		const emptyName = riskOrder.find(id => !risksMap[id]?.name.trim())
		if (emptyName) newErrors.risks = 'All risks must have a name'
		const emptyCategory = riskOrder.find(id => !risksMap[id]?.category.trim())
		if (emptyCategory) newErrors.risks = 'All risks must have a category'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setIsSaving(true)

		try {
			if (isCreateMode) {
				const result = await (dispatch as any)(
					createTemplate({
						templateName: templateName.trim(),
						description: description.trim(),
						templateType: 'risk',
						accessLevel,
					}),
				)
				const newTemplateId = result.payload?._id
				if (!newTemplateId) throw new Error('Failed to create template')

				for (let i = 0; i < riskOrder.length; i++) {
					const r = risksMap[riskOrder[i]]
					await (dispatch as any)(
						createTemplateRisk({
							templateId: newTemplateId,
							seq: i + 1,
							name: r.name.trim(),
							category: r.category.trim(),
							description: r.description.trim() || null,
							defaultLikelihood: r.defaultLikelihood,
							defaultImpact: r.defaultImpact,
							suggestedMitigation: r.suggestedMitigation.trim() || null,
							suggestedContingency: r.suggestedContingency.trim() || null,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				switchTemplate(newTemplateId, false)
				notify('Created!', `Template "${templateName.trim()}" has been created.`, 'success')
			} else if (selectedTemplateId) {
				await (dispatch as any)(
					updateTemplate({
						_id: selectedTemplateId,
						templateName: templateName.trim(),
						description: description.trim(),
						accessLevel,
					}),
				)

				// Replace-all save: same pattern as the other template builders.
				for (const origId of originalRiskIds) {
					await (dispatch as any)(
						deleteTemplateRisk({
							templateId: selectedTemplateId,
							_id: origId,
						}),
					)
				}
				for (let i = 0; i < riskOrder.length; i++) {
					const r = risksMap[riskOrder[i]]
					await (dispatch as any)(
						createTemplateRisk({
							templateId: selectedTemplateId,
							seq: i + 1,
							name: r.name.trim(),
							category: r.category.trim(),
							description: r.description.trim() || null,
							defaultLikelihood: r.defaultLikelihood,
							defaultImpact: r.defaultImpact,
							suggestedMitigation: r.suggestedMitigation.trim() || null,
							suggestedContingency: r.suggestedContingency.trim() || null,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				await (dispatch as any)(initializeTemplateRisks(selectedTemplateId))
				notify('Saved!', `Template "${templateName.trim()}" has been updated.`, 'success')
			}
			setIsDirty(false)
		} catch (err: any) {
			notify('Error', err.message || 'Failed to save template', 'error')
		} finally {
			setIsSaving(false)
		}
	}

	// ─── Derived ────────────────────────────────────────────────────────────────

	const isEditorActive = isCreateMode || selectedTemplateId !== null
	const isSaveDisabled =
		!templateName.trim() || !description.trim() || riskOrder.length === 0 || isSaving

	const categoryCount = useMemo(() => {
		const set = new Set<string>()
		for (const id of riskOrder) {
			const c = risksMap[id]?.category.trim()
			if (c) set.add(c)
		}
		return set.size
	}, [riskOrder, risksMap])

	// ─── Render ─────────────────────────────────────────────────────────────────

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">
							Risk Templates
						</h2>

						<div className="max-w-[1200px] mx-auto w-full mt-4 flex flex-row gap-4 min-h-[600px]">
							{/* ── Left Panel: Template List ────────────────────────────── */}
							<div className="w-[260px] shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col">
								<div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
									<span className="text-sm font-semibold text-gray-700">Templates</span>
									<span className="text-xs text-gray-400">{riskTemplates.length}</span>
								</div>

								<div className="flex-1 overflow-y-auto">
									{!isInitialized ? (
										<div className="flex items-center justify-center py-10">
											<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
										</div>
									) : riskTemplates.length === 0 ? (
										<div className="py-10 text-center text-sm text-gray-400">
											No templates yet
										</div>
									) : (
										riskTemplates.map((template: any) => (
											<div
												key={template._id}
												onClick={() => handleTemplateClick(template._id)}
												className={`group flex items-center justify-between px-3 py-2 cursor-pointer border-l-2 transition-colors ${
													selectedTemplateId === template._id
														? 'bg-[#0080fe]/5 text-[#0080fe] border-[#0080fe] font-semibold'
														: 'border-transparent hover:bg-gray-50 text-gray-700'
												}`}
											>
												<div className="min-w-0 flex-1">
													<div className="text-sm truncate">{template.templateName}</div>
													{template.description && (
														<div className="text-[11px] text-gray-400 truncate mt-0.5">
															{template.description}
														</div>
													)}
												</div>
												<button
													type="button"
													title="Delete template"
													onClick={e => handleDeleteTemplate(e, template._id)}
													className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-all p-1 shrink-0"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										))
									)}
								</div>

								<div className="px-3 py-2 border-t border-gray-200">
									<Button
										variant="ghost"
										onClick={handleNewTemplate}
										className="w-full justify-start"
									>
										<Plus className="w-4 h-4 mr-1.5" />
										New Template
									</Button>
								</div>
							</div>

							{/* ── Right Panel: Template Editor ─────────────────────────── */}
							<div className="flex-1 bg-white rounded-lg border border-gray-200 p-5 flex flex-col">
								{!isEditorActive ? (
									<div className="flex-1 flex flex-col items-center justify-center text-gray-400">
										<LayoutTemplate className="w-12 h-12 mb-3 text-gray-300" />
										<p className="text-sm">Select a template or create a new one</p>
									</div>
								) : (
									<>
										{/* Header Fields */}
										<div className="flex flex-col gap-2 mb-3">
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Template Name <span className="text-red-500">*</span>
												</label>
												<Input
													value={templateName}
													onChange={e => {
														setTemplateName(e.target.value)
														setIsDirty(true)
														if (errors.templateName)
															setErrors({ ...errors, templateName: '' })
													}}
													className={`text-xs h-8 ${
														errors.templateName ? 'border-red-500' : ''
													}`}
													placeholder="e.g. Common CRE risk register"
												/>
												{errors.templateName && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.templateName}
													</p>
												)}
											</div>
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Description <span className="text-red-500">*</span>
												</label>
												<Textarea
													value={description}
													onChange={e => {
														setDescription(e.target.value)
														setIsDirty(true)
														if (errors.description)
															setErrors({ ...errors, description: '' })
													}}
													className={`text-xs min-h-[3.5rem] ${
														errors.description ? 'border-red-500' : ''
													}`}
													placeholder="What kind of deal is this risk template for? When should it be applied?"
													rows={2}
												/>
												{errors.description && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.description}
													</p>
												)}
											</div>
										</div>

										{/* Access Level + Summary */}
										<div className="flex flex-row gap-4 mb-3 p-2 bg-gray-50 rounded text-xs items-center">
											<span className="font-semibold text-gray-700 mr-2">Access:</span>
											{['private', 'org-wide', 'public'].map(level => (
												<label key={level} className="flex items-center gap-1.5 cursor-pointer">
													<input
														type="radio"
														name="accessLevel"
														value={level}
														checked={accessLevel === level}
														onChange={e => {
															setAccessLevel(e.target.value)
															setIsDirty(true)
														}}
														className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
													/>
													{level === 'private'
														? 'Private'
														: level === 'org-wide'
															? 'Org-Wide'
															: 'Public'}
												</label>
											))}
											<div className="flex-1" />
											<span className="text-gray-600 flex items-center gap-1">
												<ShieldAlert className="w-3.5 h-3.5 text-[#0080fe]" />
												{riskOrder.length} risks · {categoryCount} categor
												{categoryCount === 1 ? 'y' : 'ies'}
											</span>
										</div>

										{/* Risks Table */}
										<div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-3">
											<div className="grid grid-cols-[24px_24px_32px_1fr_140px_70px_70px_60px_32px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-200 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
												<span></span>
												<span></span>
												<span className="text-center">Seq</span>
												<span>Name</span>
												<span>Category</span>
												<span className="text-center">Likelihood</span>
												<span className="text-center">Impact</span>
												<span className="text-center">Score</span>
												<label className="flex items-center justify-center cursor-pointer">
													<Checkbox
														checked={isAllSelected}
														onCheckedChange={checked => handleSelectAll(!!checked)}
													/>
												</label>
												<span></span>
											</div>

											{risksLoading && !isCreateMode ? (
												<div className="flex items-center justify-center py-8">
													<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
												</div>
											) : riskOrder.length === 0 ? (
												<div className="py-8 text-center text-sm text-gray-400">
													No risks yet. Click "+ Add Risk" to get started.
												</div>
											) : (
												<DndContext
													sensors={sensors}
													collisionDetection={closestCenter}
													onDragEnd={handleDragEnd}
												>
													<SortableContext
														items={riskOrder}
														strategy={verticalListSortingStrategy}
													>
														{riskOrder.map((id, index) => {
															const risk = risksMap[id]
															if (!risk) return null
															return (
																<SortableRow
																	key={id}
																	id={id}
																	seq={index + 1}
																	risk={risk}
																	expanded={expandedIds.has(id)}
																	onToggleExpanded={toggleExpanded}
																	onNameChange={(rid, v) => patchRisk(rid, { name: v })}
																	onCategoryChange={(rid, v) => patchRisk(rid, { category: v })}
																	onLikelihoodChange={(rid, v) =>
																		patchRisk(rid, { defaultLikelihood: v })
																	}
																	onImpactChange={(rid, v) =>
																		patchRisk(rid, { defaultImpact: v })
																	}
																	onDescriptionChange={(rid, v) =>
																		patchRisk(rid, { description: v })
																	}
																	onMitigationChange={(rid, v) =>
																		patchRisk(rid, { suggestedMitigation: v })
																	}
																	onContingencyChange={(rid, v) =>
																		patchRisk(rid, { suggestedContingency: v })
																	}
																	onDelete={handleDeleteRisk}
																	isSelected={selectedRisks.has(id)}
																	onSelect={handleSelect}
																/>
															)
														})}
													</SortableContext>
												</DndContext>
											)}
											{errors.risks && (
												<p className="text-red-500 text-[0.7rem] px-3 py-1">
													{errors.risks}
												</p>
											)}
										</div>

										{/* Actions */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2">
												<Button variant="ghost" onClick={handleAddRisk}>
													<Plus className="w-4 h-4 mr-1" />
													Add Risk
												</Button>
												{selectedRisks.size > 0 && (
													<Button
														variant="ghost-red"
														onClick={handleDeleteSelected}
													>
														<Trash2 className="w-4 h-4 mr-1" />
														Delete Selected ({selectedRisks.size})
													</Button>
												)}
											</div>
											<Button onClick={handleSave} disabled={isSaveDisabled}>
												{isSaving ? (
													<>
														<Loader2 className="w-4 h-4 mr-1 animate-spin" />
														Saving...
													</>
												) : (
													<>{isCreateMode ? 'Create Template' : 'Save Template'}</>
												)}
											</Button>
										</div>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default RiskTemplateCN
